import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Autocomplete,
  Alert,
  Chip,
  Grid,
  Button,
} from "@mui/material";
import axios from "axios";
import { getValidToken } from "../../utils/authUtils";
import { formatToLocalYMD } from "../../utils/utils";
import {
  buildMonthOnMonthMenuOptions,
  buildQuarterMenuOptions,
  DEFAULT_VEHICLE_REPORT_DATA_YEAR,
  fuelReportMonthKey,
  fuelReportPeriodSelectValue,
  fuelReportQuarterCalendarRange,
  parseQuarterPresetValue,
} from "../../utils/fuelReportPeriodUtils";
import {
  commonTextFieldProps,
  commonDateFieldProps,
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../utils/styles";
import { Loading } from "../../components/Loading";
import NavigationHeader from "../../components/NavigationHeader";

// Types based on API documentation
interface RegionalFuelData {
  vehicleNumber: string;
  model: string;
  vehicleAgeYears: number;
  district: string;
  // Bihar specific fields
  clientName?: string;
  name_of_cl?: string; // Actual field name from API
  operation?: string;
  Operation?: string; // Actual field name from API
  fuelAmount: number | string;
  litres: number | string;
  totalKmDriven?: number | string;
  mileage?: number | string;
  costPerKm?: number | string;
  // UP ALS/MVU/Punjab specific fields
  clusterLeader?: string;
  baseLocation?: string;
  edsRunKm?: number | string;
  edsMileage?: number | string;
  edsCostPerKm?: number | string;
  gpsRunKm?: number | string;
  gpsMileage?: number | string;
  gpsCostPerKm?: number | string;
}

interface EfficiencyDistribution {
  flagged: number;
  excellent: number;
  good: number;
  average: number;
  poor: number;
  no_data: number;
}

interface ReportSummary {
  total_vehicles: number;
  total_fuel_consumed: number;
  total_fuel_cost: number;
  total_km_driven?: number;
  avg_mileage: number;
  avg_cost_per_km: number;
  efficiency_distribution: EfficiencyDistribution;
}

interface ReportMetadata {
  project: string;
  procedure_used?: string;
  description?: string;
  total_records: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
  filters_applied?: Record<string, string | undefined>;
}

interface ApiResponse {
  isSuccess: boolean;
  message: string;
  data: RegionalFuelData[];
  metadata: ReportMetadata;
  summary?: ReportSummary;
  report_generated_at?: string;
}

interface FilterOptions {
  districts: string[];
  operations?: string[];
  client_names?: string[];
  cluster_leaders?: string[];
  base_locations?: string[];
  models: string[];
}

// Project configuration
const PROJECT_OPTIONS = [
  { value: "bihar", label: "Bihar" },
  { value: "up_als", label: "UP ALS" },
  { value: "up_mvu", label: "UP MVU" },
  { value: "punjab", label: "Punjab" },
];

/** Same calendar year as Vehicle Consumption month / quarter menus */
const REGIONAL_PERIOD_YEAR = DEFAULT_VEHICLE_REPORT_DATA_YEAR;

type RegionalPeriodOption = { value: string; label: string; group: string };

function buildRegionalPeriodMenuOptions(year: number): RegionalPeriodOption[] {
  const monthGroup = `Month on month (${year})`;
  const quarterGroup = `Quarter on quarter (${year})`;
  const months = buildMonthOnMonthMenuOptions(year).map((o) => ({
    value: o.value,
    label: o.label,
    group: monthGroup,
  }));
  const quarters = buildQuarterMenuOptions(year).map((o) => ({
    value: o.value,
    label: o.label,
    group: quarterGroup,
  }));
  return [
    ...months,
    ...quarters,
    {
      value: "__custom__",
      label: "Custom range (28+ days)",
      group: "Other",
    },
  ];
}

const REGIONAL_PERIOD_MENU_OPTIONS =
  buildRegionalPeriodMenuOptions(REGIONAL_PERIOD_YEAR);

const isValidRegionalCustomRange = (start: string, end: string): boolean => {
  if (!start || !end) return false;
  const s = new Date(`${start}T12:00:00`);
  const e = new Date(`${end}T12:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
  const diffDays = Math.ceil(
    Math.abs(e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)
  );
  return diffDays >= 28;
};

/** GPS vs EDS mileage difference (km/l) above which we flag for review */
const EDS_GPS_VARIANCE_REVIEW_KM = 3;

const isValidApiDateString = (value: unknown): value is string =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(`${value}T12:00:00`));

const formatDateRangeCaption = (
  start?: string,
  end?: string
): string | null => {
  if (!isValidApiDateString(start) || !isValidApiDateString(end)) {
    return null;
  }
  try {
    const opts: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    const a = new Date(`${start}T12:00:00`).toLocaleDateString(undefined, opts);
    const b = new Date(`${end}T12:00:00`).toLocaleDateString(undefined, opts);
    return `${a} → ${b}`;
  } catch {
    return null;
  }
};

// Mileage categorization function
const getMileageCategory = (mileage: number | undefined): string => {
  if (!mileage || mileage <= 0) return "no_data";
  if (mileage >= 17) return "flagged";
  if (mileage >= 15) return "excellent";
  if (mileage >= 10) return "good";
  if (mileage >= 7) return "average";
  return "poor";
};

const getMileageCategoryColor = (category: string) => {
  switch (category) {
    case "excellent":
      return "success";
    case "good":
      return "primary";
    case "average":
      return "warning";
    case "poor":
      return "error";
    case "flagged":
      return "warning";
    case "no_data":
      return "default";
    default:
      return "default";
  }
};

const getMileageCategoryLabel = (category: string) => {
  switch (category) {
    case "excellent":
      return "Excellent (≥15 km/l)";
    case "good":
      return "Good (10-14.9 km/l)";
    case "average":
      return "Average (7-10.9 km/l)";
    case "poor":
      return "Poor (<7 km/l)";
    case "flagged":
      return "Flagged (>17 km/l)";
    case "no_data":
      return "No Data";
    default:
      return "Unknown";
  }
};

// Helper function to convert string numbers to actual numbers
const parseNumericValue = (value: string | number): number => {
  if (typeof value === "number") return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to transform nested API response data
const transformApiData = (rawData: any[]): RegionalFuelData[] => {
  if (!Array.isArray(rawData)) return [];

  const transformedData: RegionalFuelData[] = [];

  rawData.forEach((item) => {
    if (
      typeof item === "object" &&
      item !== null &&
      !["fieldCount", "affectedRows"].includes(Object.keys(item)[0]) &&
      Object.keys(item).length > 0
    ) {
      // Handle nested structure like {0: {vehicleNumber: ...}, 1: {vehicleNumber: ...}, 2: {...}}
      const keys = Object.keys(item);

      // Check if all keys are numeric (indicating nested structure)
      const allNumericKeys = keys.every((key) => !isNaN(parseInt(key)));

      if (allNumericKeys && keys.length > 0) {
        // Extract all nested objects
        keys.forEach((key) => {
          const nestedData = item[key];
          if (nestedData && typeof nestedData === "object") {
            transformedData.push({
              ...nestedData,
              // Ensure numeric fields are properly converted
              vehicleAgeYears: parseNumericValue(
                nestedData.vehicleAgeYears || 0
              ),
              fuelAmount: parseNumericValue(nestedData.fuelAmount || 0),
              litres: parseNumericValue(nestedData.litres || 0),
              totalKmDriven: parseNumericValue(nestedData.totalKmDriven || 0),
              mileage: parseNumericValue(nestedData.mileage || 0),
              costPerKm: parseNumericValue(nestedData.costPerKm || 0),
              edsMileage: parseNumericValue(nestedData.edsMileage || 0),
              gpsMileage: parseNumericValue(nestedData.gpsMileage || 0),
              edsRunKm: parseNumericValue(nestedData.edsRunKm || 0),
              gpsRunKm: parseNumericValue(nestedData.gpsRunKm || 0),
              edsCostPerKm: parseNumericValue(nestedData.edsCostPerKm || 0),
              gpsCostPerKm: parseNumericValue(nestedData.gpsCostPerKm || 0),
              // Handle field name variations
              clientName: nestedData.clientName || nestedData.name_of_cl,
              operation: nestedData.operation || nestedData.Operation,
            });
          }
        });
      } else {
        // If it's already in the correct format (flat structure)
        transformedData.push({
          ...item,
          // Ensure numeric fields are properly converted
          vehicleAgeYears: parseNumericValue(item.vehicleAgeYears || 0),
          fuelAmount: parseNumericValue(item.fuelAmount || 0),
          litres: parseNumericValue(item.litres || 0),
          totalKmDriven: parseNumericValue(item.totalKmDriven || 0),
          mileage: parseNumericValue(item.mileage || 0),
          costPerKm: parseNumericValue(item.costPerKm || 0),
          edsMileage: parseNumericValue(item.edsMileage || 0),
          gpsMileage: parseNumericValue(item.gpsMileage || 0),
          edsRunKm: parseNumericValue(item.edsRunKm || 0),
          gpsRunKm: parseNumericValue(item.gpsRunKm || 0),
          edsCostPerKm: parseNumericValue(item.edsCostPerKm || 0),
          gpsCostPerKm: parseNumericValue(item.gpsCostPerKm || 0),
          // Handle field name variations
          clientName: item.clientName || item.name_of_cl,
          operation: item.operation || item.Operation,
        });
      }
    }
  });

  return transformedData.filter(Boolean); // Remove any null/undefined items
};

// Dynamic column configuration based on project
const getColumnsForProject = (project: string) => {
  const locationColumns =
    project === "up_mvu" || project === "punjab"
      ? [
          { id: "district", label: "District", minWidth: 120 },
          { id: "baseLocation", label: "Base", minWidth: 100 },
        ]
      : [{ id: "district", label: "District", minWidth: 120 }];

  const commonColumns = [
    { id: "vehicleNumber", label: "Vehicle Number", minWidth: 120 },
    { id: "model", label: "Model", minWidth: 100 },
    { id: "vehicleAgeYears", label: "Age (Years)", minWidth: 80 },
    ...locationColumns,
  ];

  if (project === "bihar") {
    return [
      ...commonColumns,
      { id: "clientName", label: "Cluster Leader", minWidth: 140 },
      { id: "operation", label: "Ops", minWidth: 100 },
      { id: "fuelAmount", label: "Fuel Cost (₹)", minWidth: 120 },
      { id: "litres", label: "Litres", minWidth: 80 },
      { id: "totalKmDriven", label: "KM Driven", minWidth: 100 },
      { id: "mileage", label: "Mileage (km/l)", minWidth: 120 },
      { id: "costPerKm", label: "Cost/KM (₹)", minWidth: 100 },
    ];
  }

  return [
    ...commonColumns,
    { id: "clusterLeader", label: "Cluster Leader", minWidth: 140 },
    { id: "fuelAmount", label: "Fuel Cost (₹)", minWidth: 120 },
    { id: "litres", label: "Litres", minWidth: 80 },
    { id: "edsMileage", label: "EDS Mileage (km/l)", minWidth: 130 },
    { id: "gpsMileage", label: "GPS Mileage (km/l)", minWidth: 130 },
    {
      id: "edsGpsVariance",
      label: "GPS − EDS (km/l)",
      minWidth: 120,
    },
    { id: "edsCostPerKm", label: "EDS Cost/KM (₹)", minWidth: 120 },
    { id: "gpsCostPerKm", label: "GPS Cost/KM (₹)", minWidth: 120 },
  ];
};

const RegionalFuelReportPage: React.FC = () => {
  const [data, setData] = useState<RegionalFuelData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(
    null
  );
  const [reportMetadata, setReportMetadata] = useState<ReportMetadata | null>(
    null
  );
  const [reportGeneratedAt, setReportGeneratedAt] = useState<string | null>(
    null
  );

  // Filter states
  const [project, setProject] = useState<string>("bihar");
  const [dateFilter, setDateFilter] = useState<string>(() =>
    fuelReportMonthKey(REGIONAL_PERIOD_YEAR, 1)
  );
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [district, setDistrict] = useState<string>("");
  const [model, setModel] = useState<string>("");

  // Bihar specific filters
  const [clientName, setClientName] = useState<string>("");
  const [operation, setOperation] = useState<string>("");

  // UP ALS/MVU/Punjab specific filters
  const [clusterLeaderName, setClusterLeaderName] = useState<string>("");
  const [baseLocation, setBaseLocation] = useState<string>("");

  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    districts: [],
    models: [],
  });

  const selectedPeriodOption = useMemo(() => {
    const key = fuelReportPeriodSelectValue(
      { dateFilter, startDate, endDate },
      REGIONAL_PERIOD_YEAR
    );
    return (
      REGIONAL_PERIOD_MENU_OPTIONS.find((o) => o.value === key) ??
      REGIONAL_PERIOD_MENU_OPTIONS[0]
    );
  }, [dateFilter, startDate, endDate]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}fuel-reports/regional-sp/filter-options?project=${project}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.isSuccess) {
        setFilterOptions(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  }, [project]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      queryParams.set("project", project);

      const isCalendarMonth = /^\d{4}-\d{2}$/.test(dateFilter);

      if (isCalendarMonth) {
        queryParams.set("dateFilter", dateFilter);
      } else if (dateFilter === "custom") {
        if (!startDate || !endDate) {
          setData([]);
          setReportSummary(null);
          setReportMetadata(null);
          setReportGeneratedAt(null);
          setError(
            "Select both start and end dates, or choose a calendar month / quarter above."
          );
          return;
        }
        if (!isValidRegionalCustomRange(startDate, endDate)) {
          setData([]);
          setReportSummary(null);
          setReportMetadata(null);
          setReportGeneratedAt(null);
          setError(
            "Custom range: at least 28 days between start and end (same rule as other fuel reports)."
          );
          return;
        }
        queryParams.set("dateFilter", "custom");
        queryParams.set("startDate", startDate);
        queryParams.set("endDate", endDate);
      } else {
        queryParams.set("dateFilter", dateFilter);
      }

      if (district) queryParams.set("district", district);
      if (model) queryParams.set("model", model);
      if (clientName) queryParams.set("clientName", clientName);
      if (operation) queryParams.set("operation", operation);
      if (clusterLeaderName)
        queryParams.set("clusterLeaderName", clusterLeaderName);
      if (baseLocation) queryParams.set("baseLocation", baseLocation);

      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}fuel-reports/regional-sp?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result: ApiResponse = response.data;

      if (!result.isSuccess) {
        throw new Error(result.message || "Failed to fetch data");
      }

      // Transform the API data to handle nested structures
      const transformedData = transformApiData(result.data);

      setData(transformedData);
      setReportSummary(result.summary ?? null);
      setReportMetadata(result.metadata ?? null);
      setReportGeneratedAt(result.report_generated_at ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setReportSummary(null);
      setReportMetadata(null);
      setReportGeneratedAt(null);
      console.error("Error fetching regional fuel report:", err);
    } finally {
      setLoading(false);
    }
  }, [
    project,
    dateFilter,
    startDate,
    endDate,
    district,
    model,
    clientName,
    operation,
    clusterLeaderName,
    baseLocation,
  ]);

  // Effects
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Event handlers
  const applyProjectChange = (nextProject: string) => {
    setProject(nextProject);
    setModel("");
    setClientName("");
    setOperation("");
    setClusterLeaderName("");
    setBaseLocation("");
  };

  const handleRegionalPeriodChange = (value: string) => {
    if (value === "__custom__") {
      setDateFilter("custom");
      setStartDate("");
      setEndDate("");
      return;
    }
    const quarter = parseQuarterPresetValue(value);
    if (quarter) {
      const { start, end } = fuelReportQuarterCalendarRange(
        quarter.year,
        quarter.quarter
      );
      setDateFilter("custom");
      setStartDate(formatToLocalYMD(start));
      setEndDate(formatToLocalYMD(end));
      return;
    }
    setDateFilter(value);
    setStartDate("");
    setEndDate("");
  };

  const clearFilters = () => {
    setDateFilter(fuelReportMonthKey(REGIONAL_PERIOD_YEAR, 1));
    setStartDate("");
    setEndDate("");
    setDistrict("");
    setModel("");
    setClientName("");
    setOperation("");
    setClusterLeaderName("");
    setBaseLocation("");
  };

  const columns = getColumnsForProject(project);

  const renderCellValue = (column: any, row: RegionalFuelData) => {
    if (column.id === "edsGpsVariance") {
      const eds = parseNumericValue(row.edsMileage as string | number);
      const gps = parseNumericValue(row.gpsMileage as string | number);
      if (!eds || !gps) {
        return "-";
      }
      const delta = gps - eds;
      const needsReview = Math.abs(delta) >= EDS_GPS_VARIANCE_REVIEW_KM;
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {`${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`}
          </Typography>
          {needsReview && (
            <Chip
              label="Review"
              color="warning"
              size="small"
              sx={{ fontSize: "0.6rem", height: 20 }}
            />
          )}
        </Box>
      );
    }

    let value = row[column.id as keyof RegionalFuelData];

    // Handle field name variations for display
    if (column.id === "clientName" && !value) {
      value = row.name_of_cl;
    }
    if (column.id === "operation" && !value) {
      value = row.Operation;
    }

    // Handle mileage columns with categorization
    if (
      column.id === "mileage" ||
      column.id === "edsMileage" ||
      column.id === "gpsMileage"
    ) {
      const mileageValue = parseNumericValue(value as any);
      const category = getMileageCategory(mileageValue);

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {mileageValue ? mileageValue.toFixed(2) : "-"}
          </Typography>
          {mileageValue > 0 && (
            <Chip
              label={category.charAt(0).toUpperCase() + category.slice(1)}
              color={getMileageCategoryColor(category) as any}
              size="small"
              sx={{ fontSize: "0.6rem", height: 20 }}
            />
          )}
        </Box>
      );
    }

    // Handle currency fields
    if (
      column.id === "fuelAmount" ||
      column.id === "costPerKm" ||
      column.id === "edsCostPerKm" ||
      column.id === "gpsCostPerKm"
    ) {
      const numValue = parseNumericValue(value as any);
      return numValue ? `₹${numValue.toFixed(2)}` : "-";
    }

    // Handle numeric fields
    if (typeof value === "number") {
      return value.toFixed(2);
    }

    // Handle string numeric fields
    if (typeof value === "string" && !isNaN(parseFloat(value))) {
      return parseFloat(value).toFixed(2);
    }

    return value || "-";
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <NavigationHeader
        title="Regional Fuel Reports"
        backNavigationEnabled={true}
      />

      {/* Filter Panel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Project Selection */}
          <Grid item xs={12} md={2}>
            <Autocomplete
              size="small"
              options={PROJECT_OPTIONS}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={
                PROJECT_OPTIONS.find((o) => o.value === project) ??
                PROJECT_OPTIONS[0]
              }
              onChange={(_, opt) => {
                if (opt) applyProjectChange(opt.value);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Project" {...commonTextFieldProps} />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {option.label}
                  </Typography>
                </li>
              )}
            />
          </Grid>

          {/* Period: same month / quarter structure as Vehicle Consumption */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={REGIONAL_PERIOD_MENU_OPTIONS}
              groupBy={(opt) => opt.group}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={selectedPeriodOption}
              onChange={(_, opt) => {
                if (opt) handleRegionalPeriodChange(opt.value);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Period" {...commonTextFieldProps} />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {option.label}
                  </Typography>
                </li>
              )}
            />
          </Grid>

          {dateFilter === "custom" && (
            <>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonDateFieldProps}
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonDateFieldProps}
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>
            </>
          )}

          {/* District Filter */}
          <Grid item xs={12} md={1.5}>
            <Autocomplete
              size="small"
              options={["", ...(filterOptions.districts ?? [])]}
              value={district}
              onChange={(_, v) => setDistrict(v ?? "")}
              getOptionLabel={(o) => (o === "" ? "All districts" : o)}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => (
                <TextField {...params} label="District" {...commonTextFieldProps} />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {option === "" ? "All districts" : option}
                  </Typography>
                </li>
              )}
            />
          </Grid>

          {/* Model (all projects) */}
          <Grid item xs={12} md={1.5}>
            <Autocomplete
              size="small"
              options={["", ...(filterOptions.models ?? [])]}
              value={model}
              onChange={(_, v) => setModel(v ?? "")}
              getOptionLabel={(o) => (o === "" ? "All models" : o)}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => (
                <TextField {...params} label="Model" {...commonTextFieldProps} />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {option === "" ? "All models" : option}
                  </Typography>
                </li>
              )}
            />
          </Grid>

          {/* Bihar specific filters */}
          {project === "bihar" && (
            <>
              <Grid item xs={12} md={1.5}>
                <Autocomplete
                  size="small"
                  options={["", ...(filterOptions.client_names ?? [])]}
                  value={clientName}
                  onChange={(_, v) => setClientName(v ?? "")}
                  getOptionLabel={(o) =>
                    o === "" ? "All cluster leaders" : o
                  }
                  isOptionEqualToValue={(a, b) => a === b}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cluster Leader"
                      {...commonTextFieldProps}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {option === "" ? "All cluster leaders" : option}
                      </Typography>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <Autocomplete
                  size="small"
                  options={["", ...(filterOptions.operations ?? [])]}
                  value={operation}
                  onChange={(_, v) => setOperation(v ?? "")}
                  getOptionLabel={(o) => (o === "" ? "All operations" : o)}
                  isOptionEqualToValue={(a, b) => a === b}
                  renderInput={(params) => (
                    <TextField {...params} label="Operation" {...commonTextFieldProps} />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {option === "" ? "All operations" : option}
                      </Typography>
                    </li>
                  )}
                />
              </Grid>
            </>
          )}

          {/* UP ALS/MVU/Punjab specific filters */}
          {project !== "bihar" && (
            <>
              <Grid item xs={12} md={1.5}>
                <Autocomplete
                  size="small"
                  options={["", ...(filterOptions.cluster_leaders ?? [])]}
                  value={clusterLeaderName}
                  onChange={(_, v) => setClusterLeaderName(v ?? "")}
                  getOptionLabel={(o) => (o === "" ? "All leaders" : o)}
                  isOptionEqualToValue={(a, b) => a === b}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cluster Leader"
                      {...commonTextFieldProps}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {option === "" ? "All leaders" : option}
                      </Typography>
                    </li>
                  )}
                />
              </Grid>
              {(project === "up_mvu" || project === "punjab") && (
                <Grid item xs={12} md={1.5}>
                  <Autocomplete
                    size="small"
                    options={["", ...(filterOptions.base_locations ?? [])]}
                    value={baseLocation}
                    onChange={(_, v) => setBaseLocation(v ?? "")}
                    getOptionLabel={(o) => (o === "" ? "All bases" : o)}
                    isOptionEqualToValue={(a, b) => a === b}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Base location"
                        {...commonTextFieldProps}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Typography sx={{ fontSize: "0.75rem" }}>
                          {option === "" ? "All bases" : option}
                        </Typography>
                      </li>
                    )}
                  />
                </Grid>
              )}
            </>
          )}

          {/* Clear Filters Button */}
          <Grid item xs={12} md={1} sx={{ marginLeft: "auto" }}>
            <Button
              {...smallButtonProp}
              variant="outlined"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {dateFilter === "custom" &&
          !isValidRegionalCustomRange(startDate, endDate) && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mt: 2, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                Custom range: choose start and end dates at least 28 days apart
                (same as Vehicle Consumption).
              </Typography>
            </Alert>
          )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Executive summary (API summary + metadata) */}
      {!loading && reportSummary && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Period summary
          </Typography>
          {reportMetadata?.date_range && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1.5 }}
            >
              {formatDateRangeCaption(
                reportMetadata.date_range.start_date,
                reportMetadata.date_range.end_date
              ) ?? (
                <>
                  Invalid date range in response (
                  {String(reportMetadata.date_range.start_date)} →{" "}
                  {String(reportMetadata.date_range.end_date)})
                </>
              )}
              {reportGeneratedAt &&
                !Number.isNaN(Date.parse(reportGeneratedAt)) &&
                ` · Generated ${new Date(
                  reportGeneratedAt
                ).toLocaleString()}`}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Vehicles
              </Typography>
              <Typography variant="h6">
                {reportSummary.total_vehicles}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Fuel cost
              </Typography>
              <Typography variant="h6">
                ₹{reportSummary.total_fuel_cost.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Litres
              </Typography>
              <Typography variant="h6">
                {reportSummary.total_fuel_consumed.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Distance (km)
              </Typography>
              <Typography variant="h6">
                {(reportSummary.total_km_driven ?? 0).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }
                )}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Avg mileage (km/l)
              </Typography>
              <Typography variant="h6">
                {reportSummary.avg_mileage.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Avg cost / km
              </Typography>
              <Typography variant="h6">
                ₹{reportSummary.avg_cost_per_km.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            Fleet mix by GPS mileage band (same rules as table chips)
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
            {(
              [
                "flagged",
                "excellent",
                "good",
                "average",
                "poor",
                "no_data",
              ] as const
            ).map((bucket) => {
              const count =
                reportSummary.efficiency_distribution[bucket] ?? 0;
              return (
                <Chip
                  key={bucket}
                  size="small"
                  label={`${getMileageCategoryLabel(bucket)}: ${count}`}
                  color={getMileageCategoryColor(bucket) as "default"}
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Data Table */}
      <Paper sx={{ width: "100%", mb: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <Loading fullScreen={false} message="Loading fuel report..." />
          </Box>
        )}

        <TableContainer sx={{ overflowY: "hidden" }}>
          <Table
            size="small"
            stickyHeader
            aria-label="regional fuel report table"
          >
            <TableHead sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      ...tableHeaderStyles,
                      fontSize: "0.75rem",
                      minWidth: column.minWidth,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      sx={{ py: 3 }}
                    >
                      No data available for the selected filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={{ ...tableCellStyles }}>
                        {renderCellValue(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Mileage Category Legend */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Mileage Categories:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {[
            "excellent",
            "good",
            "average",
            "poor",
            "flagged",
            "no_data",
          ].map((category) => (
            <Chip
              key={category}
              label={getMileageCategoryLabel(category)}
              color={getMileageCategoryColor(category) as "default"}
              size="small"
              sx={{ fontSize: "0.7rem" }}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default RegionalFuelReportPage;
